<template>
  <MultiStepDialog
    ref="dialogRef"
    :steps="steps"
    variant="create"
    background="white"
    :size="maxSmall ? 'full' : undefined"
    :title="$t('components.schedule_task')"
    :withClose="false"
    :cancelLabel="
      dialogRef?.currentStep === 'date'
        ? $t('components.buttons.back')
        : undefined
    "
    :confirmLabel="
      dialogRef?.currentStep === 'assignees'
        ? $t('components.buttons.save')
        : undefined
    "
    :disableConfirm="disableConfirm"
    :closeOnCancel="dialogRef?.currentStep === 'assignees'"
    :closeOnConfirm="dialogRef?.currentStep === 'date'"
    @update:open="form.reset()"
    @cancel="handleCancel()"
    @confirm="componentRef?.submit()"
  >
    <template #content="{ step, props, handleGoToStep }">
      <Component
        ref="componentRef"
        :is="step.content"
        v-bind="props"
        :query="dialogRef?.query"
        class="min-h-[350px] grow overflow-y-auto"
        @goToStep="handleGoToStep"
        @change="handleUpdateForm"
        @submit="handleSubmit"
        @clear:query="dialogRef?.clearQuery()"
      />
    </template>
  </MultiStepDialog>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";
import { useForm } from "@inertiajs/vue3";
import { MultiStepDialog } from "@/SharedComponents/Dialogs";
import { Date, Users } from "@/SharedComponents/Modals/Content";
import { Steps } from "@/Composables/useSteps";
import { useI18n } from "vue-i18n";

const dialogRef = ref(null);
const componentRef = ref(null);

const props = defineProps({
  task: { type: Object, required: false },
});

const form = useForm({
  assignees: [],
  date: null,
});

watch(
  () => props.task,
  () => {
    if (props.task) {
      form.assignees = [];
      form.date = null;
      form.defaults();
    }
  }
);

const buildingIds = computed(() => (props.task ? [props.task.tenant.id] : []));

const { t } = useI18n();

const steps = computed(
  () =>
    new Steps({
      assignees: {
        withSearch: true,
        content: Users,
        props: {
          selectedUsers: form.assignees,
          buildingIds: buildingIds.value,
          class: "py-6 px-4 border-secondary bg-secondary border-t",
        },
      },
      date: {
        title: t("components.asset_create.steps.space_select_title"),
        content: Date,
        props: {
          selectedDate: form.date,
          buildingIds: buildingIds.value,
          required: false,
          class: "py-6 px-4 mt-5 border-secondary border-t",
        },
      },
    })
);

const disableConfirm = computed(() => {
  return (
    dialogRef.value?.currentStep !== "date" && !componentRef.value?.isDirty
  );
});

const emitter = inject("emitter");

function handleCancel() {
  if (dialogRef.value?.currentStep === "assignees") {
    emitter.emit("cancel-scheduling-task");
  } else {
    dialogRef.value?.handleStepBack();
  }
}

function handleUpdateForm(value) {
  form[dialogRef.value?.currentStep] = value;
}

// eslint-disable-next-line no-unused-vars
function handleSubmit(_) {
  if (dialogRef.value?.currentStep === "date") {
    emitter.emit("confirm-scheduling-task", {
      assignees: form.assignees.map((assignee) => assignee.id),
      date: form.date,
    });
  } else {
    dialogRef.value?.handleStepForward();
  }
}
</script>
